/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {ApiConfig} from '../../api';
import {ASSET_ERROR} from '../resources-constants';
import {AdnHttpProgressCallback, AdnHttpService} from '../../../util/adn-http.service';
import {
  createAdnHttpResource,
  createAdnHttpResourceSender,
  createAdnHttpResponseSender,
  createAdnHttpUploadSender,
  createAdnUploadFormData
} from '../../../util/create-adn-http-resource';
import {LocalUserProfile} from '../../../session/local-user-profile';
import {AuthHttpInterceptorService} from '../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../app/common/directives/error/api-error-handler.service';

/** Angular DI token for the ThirdPartyAudience HTTP resource (factory product). */
export class ThirdPartyAudienceResource {
}

export function createThirdPartyAudienceResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile
): any {
  const send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  const sendResponse = createAdnHttpResponseSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  const sendUpload = createAdnHttpUploadSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  const ThirdPartyAudience: any = createAdnHttpResource(
    send,
    'thirdpartyaudiences',
    () => localUserProfile,
    'thirdPartyAudiences'
  );

  function upload(
    path: string[],
    data: any,
    params: any,
    onProgress?: AdnHttpProgressCallback
  ): Promise<any> {
    if (!data || !(data.file || data.content)) {
      return Promise.reject(ASSET_ERROR.NO_FILE);
    }
    return sendUpload({
      method: 'POST',
      url: ApiConfig.obtain().getVersionedUri(path),
      params,
      body: createAdnUploadFormData(data)
    }, onProgress);
  }

  ThirdPartyAudience.uploadAudienceDefinitions = function(
    data: any,
    params: any,
    onProgress?: AdnHttpProgressCallback
  ): Promise<any> {
    return upload(['thirdpartyaudiences'], data, _.assign({
      delimiter: ',',
      externalReferenceField: 0,
      providerField: -1,
      nameField: '1',
      descriptionField: 2,
      objectStateField: -1,
      teamField: 3,
      objectState: 'ACTIVE'
    }, params), onProgress);
  };

  ThirdPartyAudience.uploadAudienceData = function(
    data: any,
    params: any,
    onProgress?: AdnHttpProgressCallback
  ): Promise<any> {
    return upload(['thirdpartyaudiences', 'postcodes'], data, _.assign({
      delimiter: ',',
      format: 'POSTCODE_LIST'
    }, params), onProgress);
  };

  ThirdPartyAudience.uploadPostcodes = function(
    id: any,
    postcodes: any,
    countryCode: any,
    params: any
  ): Promise<any> {
    return sendResponse({
      method: 'POST',
      url: ApiConfig.obtain().getVersionedUri(['thirdpartyaudiences', id, 'postcodes']),
      params: _.assign({}, params),
      body: {
        postcodes,
        countryIsoCode: countryCode
      }
    });
  };

  return ThirdPartyAudience;
}

export const thirdPartyAudienceResourceProvider = {
  provide: ThirdPartyAudienceResource,
  useFactory: createThirdPartyAudienceResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile]
};
